import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
  FlaskConical, Eye, Crown, Users, UserCheck, RefreshCw, 
  CheckCircle, XCircle, AlertTriangle, Shield
} from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

const SUBSCRIPTION_TIERS = {
  free: { name: 'Free', color: 'bg-gray-500', maxUsers: 0 },
  trial: { name: 'Trial', color: 'bg-blue-500', maxUsers: 1 },
  individual: { name: 'Individual ($1.99/mo)', color: 'bg-green-500', maxUsers: 1 },
  family: { name: 'Family & Friends ($2.99/mo)', color: 'bg-purple-500', maxUsers: 4 },
  cookout: { name: 'The Whole Cookout ($5.99/mo)', color: 'bg-orange-500', maxUsers: 8 },
  reunion: { name: 'Family Reunion ($12.99/mo)', color: 'bg-pink-500', maxUsers: 20 },
  admin: { name: 'Admin (Unlimited)', color: 'bg-red-500', maxUsers: 999 }
};

const TEST_SCENARIOS = [
  {
    id: 'free_user',
    name: 'Free User Experience',
    description: 'Test what users see with limited free access (10 phrase views)',
    status: 'free',
    tier: null,
    checks: [
      'Can view up to 10 phrases',
      'Sees upgrade prompts after limit',
      'Cannot access premium features',
      'Subscription page shows all tiers'
    ]
  },
  {
    id: 'trial_user',
    name: 'Trial User Experience',
    description: 'Test what users see during their 7-day free trial',
    status: 'trial',
    tier: 'individual',
    checks: [
      'Unlimited phrase access',
      'Trial badge in header',
      'Trial end date visible',
      'Can cancel without charge'
    ]
  },
  {
    id: 'individual_sub',
    name: 'Individual Subscriber',
    description: 'Test individual subscription experience',
    status: 'active',
    tier: 'individual',
    checks: [
      'Unlimited phrase access',
      'VIP badge in header',
      'No member management (single user)',
      'Can cancel subscription'
    ]
  },
  {
    id: 'family_owner',
    name: 'Family Plan Owner',
    description: 'Test family subscription owner experience',
    status: 'active',
    tier: 'family',
    checks: [
      'Can invite up to 4 members',
      'Member management visible',
      'Text/Share invite options',
      'Can remove members'
    ]
  },
  {
    id: 'family_member',
    name: 'Family Plan Member',
    description: 'Test experience as an invited family member',
    status: 'active',
    tier: 'family',
    isMember: true,
    checks: [
      'Unlimited phrase access',
      'Shows "via owner@email.com"',
      'Cannot manage members',
      'Cannot cancel (only owner can)'
    ]
  },
  {
    id: 'cookout_owner',
    name: 'Cookout Plan Owner',
    description: 'Test cookout subscription (8 users) experience',
    status: 'active',
    tier: 'cookout',
    checks: [
      'Can invite up to 8 members',
      'Member management visible',
      'Shows member count',
      'Text/Share invite options'
    ]
  },
  {
    id: 'reunion_owner',
    name: 'Reunion Plan Owner',
    description: 'Test family reunion subscription (20 users) experience',
    status: 'active',
    tier: 'reunion',
    checks: [
      'Can invite up to 20 members',
      'Large member list management',
      'Bulk invite options',
      'Text/Share invite options'
    ]
  },
  {
    id: 'expired_user',
    name: 'Expired Subscription',
    description: 'Test what users see when subscription expires',
    status: 'expired',
    tier: 'individual',
    checks: [
      'Shows expired status',
      'Limited to free tier access',
      'Prompts to renew',
      'Previous tier info shown'
    ]
  }
];

export default function AdminTestEnvironment() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, refreshUser } = useAuth();
  
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulatedStatus, setSimulatedStatus] = useState(null);
  const [testModeActive, setTestModeActive] = useState(false);

  // Check admin access and test mode status
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/');
      toast.error('Admin access required');
    }
    
    // Check if test mode is already active
    checkTestModeStatus();
  }, [authLoading, isAuthenticated, user, navigate]);

  const checkTestModeStatus = async () => {
    try {
      const { data, error } = await apiFetch('/api/admin/test-mode/status');
      if (!error && data) {
        setTestModeActive(data.test_mode_active);
        if (data.test_mode_active) {
          setSimulatedStatus({
            status: data.current_status,
            tier: data.current_tier,
            original: data.original_data
          });
        }
      }
    } catch (error) {
      console.error('Failed to check test mode status:', error);
    }
  };

  const handleSelectScenario = (scenario) => {
    setSelectedScenario(scenario);
    setTestResults({});
  };

  const handleSimulate = async () => {
    if (!selectedScenario) return;
    
    setIsSimulating(true);
    
    try {
      const { data, error } = await apiFetch('/api/admin/test-mode/enable', {
        method: 'POST',
        body: {
          status: selectedScenario.status,
          tier: selectedScenario.tier,
          is_owner: !selectedScenario.isMember,
          phrases_viewed: selectedScenario.status === 'free' ? 5 : 0
        }
      });
      
      if (!error && data) {
        setSimulatedStatus(data.simulated);
        setTestModeActive(true);
        
        // Refresh user data to reflect changes
        if (refreshUser) {
          await refreshUser();
        }
        
        // Run test checks
        const results = {};
        selectedScenario.checks.forEach((check, index) => {
          setTimeout(() => {
            results[index] = { passed: true, check };
            setTestResults({ ...results });
          }, (index + 1) * 300);
        });
        
        setTimeout(() => {
          toast.success('Test mode enabled! Your account is now simulating this subscription.');
        }, selectedScenario.checks.length * 300 + 200);
      } else {
        toast.error(error || 'Failed to enable test mode');
      }
    } catch (error) {
      toast.error('Failed to enable test mode');
    } finally {
      setIsSimulating(false);
    }
  };

  const handleOpenAsUser = () => {
    // Open home page in new tab - user will see their simulated subscription
    window.open('/', '_blank');
    toast.info('Opened app with simulated subscription. Check the new tab.');
  };

  const handleClearTestMode = async () => {
    try {
      const { data, error } = await apiFetch('/api/admin/test-mode/disable', {
        method: 'POST'
      });
      
      if (!error) {
        setSimulatedStatus(null);
        setSelectedScenario(null);
        setTestResults({});
        setTestModeActive(false);
        
        // Refresh user data to restore original status
        if (refreshUser) {
          await refreshUser();
        }
        
        toast.success('Test mode disabled. Your original subscription is restored.');
      } else {
        toast.error('Failed to disable test mode');
      }
    } catch (error) {
      toast.error('Failed to disable test mode');
    }
  };

  if (authLoading || !user?.is_admin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="h-8 w-8 animate-spin text-[#00E5CC]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <FlaskConical className="h-8 w-8 text-purple-500" />
              Admin Test Environment
            </h1>
            <p className="text-gray-600 mt-1">Test subscription features and user experiences</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/admin')}>
              <Shield className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            {testModeActive && (
              <Button variant="destructive" onClick={handleClearTestMode}>
                Disable Test Mode
              </Button>
            )}
          </div>
        </div>

        {testModeActive ? (
          <Alert className="mb-6 border-orange-300 bg-orange-50">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-700">
              <strong>Test Mode Active!</strong> Your account is currently simulating a different subscription status. 
              Navigate the app to test features. Click &quot;Disable Test Mode&quot; when done to restore your admin status.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="mb-6 border-purple-300 bg-purple-50">
            <FlaskConical className="h-4 w-4 text-purple-600" />
            <AlertDescription className="text-purple-700">
              <strong>Test Environment:</strong> Use this page to simulate different subscription scenarios 
              and test how the app behaves for different user types. Your subscription will be temporarily changed.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Scenario Selection */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Test Scenarios</CardTitle>
                <CardDescription>Select a scenario to test</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {TEST_SCENARIOS.map((scenario) => (
                  <div
                    key={scenario.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedScenario?.id === scenario.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    onClick={() => handleSelectScenario(scenario)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{scenario.name}</span>
                      <Badge className={SUBSCRIPTION_TIERS[scenario.status]?.color || 'bg-gray-500'}>
                        {scenario.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500">{scenario.description}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Test Details & Results */}
          <div className="lg:col-span-2 space-y-6">
            {selectedScenario ? (
              <>
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{selectedScenario.name}</CardTitle>
                        <CardDescription>{selectedScenario.description}</CardDescription>
                      </div>
                      <Badge className={SUBSCRIPTION_TIERS[selectedScenario.tier || selectedScenario.status]?.color}>
                        {selectedScenario.tier 
                          ? SUBSCRIPTION_TIERS[selectedScenario.tier]?.name 
                          : selectedScenario.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium mb-2">Test Checklist:</h4>
                        <ul className="space-y-2">
                          {selectedScenario.checks.map((check, index) => (
                            <li key={index} className="flex items-center gap-2 text-sm">
                              {testResults[index] ? (
                                testResults[index].passed ? (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-500" />
                                )
                              ) : (
                                <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                              )}
                              {check}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="flex gap-2 pt-4 border-t">
                        <Button 
                          onClick={handleSimulate} 
                          disabled={isSimulating}
                          className="flex-1"
                        >
                          {isSimulating ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Running Tests...
                            </>
                          ) : (
                            <>
                              <FlaskConical className="h-4 w-4 mr-2" />
                              Run Simulation
                            </>
                          )}
                        </Button>
                        {simulatedStatus && (
                          <Button variant="outline" onClick={handleOpenAsUser}>
                            <Eye className="h-4 w-4 mr-2" />
                            Preview as User
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Simulated Status Display */}
                {(simulatedStatus || testModeActive) && (
                  <Card className="border-orange-300">
                    <CardHeader className="bg-orange-50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-orange-500" />
                        Test Mode Active - Simulated Subscription
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Status</p>
                          <Badge className={SUBSCRIPTION_TIERS[simulatedStatus?.status]?.color || 'bg-gray-500'}>
                            {simulatedStatus?.status || 'unknown'}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Tier</p>
                          <p className="font-medium">
                            {simulatedStatus?.tier 
                              ? SUBSCRIPTION_TIERS[simulatedStatus.tier]?.name 
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Max Users</p>
                          <p className="font-medium">
                            {simulatedStatus?.tier 
                              ? SUBSCRIPTION_TIERS[simulatedStatus.tier]?.maxUsers 
                              : 1}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Is Owner</p>
                          <p className="font-medium">{simulatedStatus?.is_owner !== false ? 'Yes' : 'No (Member)'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Phrases Viewed</p>
                          <p className="font-medium">{simulatedStatus?.phrases_viewed || 0}</p>
                        </div>
                      </div>
                      
                      {simulatedStatus?.trial_ends_at && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-700">
                            <strong>Trial Ends:</strong> {new Date(simulatedStatus.trial_ends_at).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      
                      {simulatedStatus?.subscription_ends_at && (
                        <div className="mt-4 p-3 bg-green-50 rounded-lg">
                          <p className="text-sm text-green-700">
                            <strong>Renews:</strong> {new Date(simulatedStatus.subscription_ends_at).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Your admin privileges are preserved.</strong> Click &quot;Disable Test Mode&quot; to restore your original subscription.
                        </p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={handleOpenAsUser}>
                            <Eye className="h-4 w-4 mr-2" />
                            Open App (New Tab)
                          </Button>
                          <Button variant="destructive" size="sm" onClick={handleClearTestMode}>
                            Disable Test Mode
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center text-gray-500">
                    <FlaskConical className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select a test scenario from the left to begin testing</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Test Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Button variant="outline" size="sm" onClick={() => navigate('/subscription')}>
                    <Crown className="h-4 w-4 mr-2" />
                    View Subscription Page
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Homepage
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open('/phrase/1', '_blank')}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Phrase Detail
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate('/admin')}>
                    <Users className="h-4 w-4 mr-2" />
                    Manage Users
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
